import React, { useEffect, useState } from 'react';
import { PlayCircle, Camera, MessageCircle, Share2, Heart, Plus } from 'lucide-react'
import './App.css';
import { db, auth, storage } from './firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { signOut } from 'firebase/auth';
import { doc, setDoc, deleteDoc, getDocs, collection, query, collectionGroup, where } from 'firebase/firestore';
import { enviarNotificacao } from "./notificacoesService";
import Levantamentos from './Levantamentos';
import { observarSaldo } from './saldoUtilizador';
import { calcularNivel, obterImagemAdepto, obterImagemJogador } from './tabelaNiveis';
import { ModalAdepto, ModalJogador } from './ModaisNivel';
import ConquistasFuturas from './ConquistasFuturas';
import InfoNiveis from './InfoNiveis';

import imgFundoPerfil from './assets/fundos/fundoPerfil.webp';

const Perfil = ({ userDados: userDadosProp, abaGrelha, setAbaGrelha, postsDoFeed, darLike, presenteAtivo, setPresenteAtivo, setMostrarPresentes, setIdPostAtivo, setEcraAtivo }) => {

  // =====================================================================
  // REDE DE SEGURANÇA DO ALVO DO PERFIL
  // ---------------------------------------------------------------------
  // O perfil de outro craque chega sempre com `uid` (o Feed monta-o à mão).
  // Sem `uid` só há um caso possível: é o MEU perfil e a ficha do Firestore
  // ainda não chegou (ou veio sem o campo `uid`, o que acontece em fichas
  // antigas). Antes, isso deixava o ecrã encravado no cartão preto
  // "A CARREGAR O PERFIL..." — que no telemóvel se lê como ecrã preto.
  // Com o Auth já resolvido temos uid, nome e foto para desenhar já.
  // =====================================================================
  const userDados = React.useMemo(() => {
    if (userDadosProp?.uid) return userDadosProp;
    const eu = auth.currentUser;
    if (!eu) return userDadosProp;
    return {
      ...(userDadosProp || {}),
      uid: eu.uid,
      nomeCompleto: userDadosProp?.nomeCompleto || eu.displayName || "Craque",
      fotoUrl: userDadosProp?.fotoUrl || eu.photoURL || "",
    };
  }, [userDadosProp]);

  const nivelAdepto = calcularNivel(userDados?.xpAdepto || 0);
  const nivelJogador = calcularNivel(userDados?.xpJogador || 0);

  const [modalAdeptoAberta, setModalAdeptoAberta] = useState(false);
  const [modalJogadorAberta, setModalJogadorAberta] = useState(false);

  // =====================================================================
  // DECLARAÇÃO DE ESTADOS (SEMPRE NO TOPO DA FUNÇÃO)
  // =====================================================================
  const [meusPosts, setMeusPosts] = React.useState([]);
  const [postParaVerLikes, setPostParaVerLikes] = useState(null);
  const [postSelecionado, setPostSelecionado] = React.useState(null);

  // =====================================================================
  // MOTOR 1: FORÇAR ABERTURA DE POST VINDO DE LINK EXTERNO
  // =====================================================================
  React.useEffect(() => {
    if (userDados?.postParaAbrirForçado) {
      const post = userDados.postParaAbrirForçado;
      setPostSelecionado(post);
      setIdPostAtivo(post);
      userDados.postParaAbrirForçado = null; // Limpa para não repetir
    }
  }, [userDados, setIdPostAtivo]);

  // =====================================================================
  // MOTOR 2: CARREGADOR DE POSTS DO UTILIZADOR EM TEMPO REAL
  // =====================================================================
  React.useEffect(() => {
    if (!userDados?.uid) return;
    let unsub;

    const carregarEmTempoReal = async () => {
      const { collection, query, where, onSnapshot } = await import('firebase/firestore');
      const q = query(collection(db, "posts"), where("userId", "==", userDados.uid));

      unsub = onSnapshot(q, (snap) => {
        const lista = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const listaOrganizada = lista.sort((a, b) => (b.criadoEm?.seconds || 0) - (a.criadoEm?.seconds || 0));
        setMeusPosts(listaOrganizada);
      });
    };

    const escutarOrdemDeAbrirPost = (e) => {
      if (e.detail) {
        setPostSelecionado(e.detail);
        setIdPostAtivo(e.detail);
      }
    };

    window.addEventListener('abrirComentariosPerfil', escutarOrdemDeAbrirPost);
    carregarEmTempoReal();

    return () => {
      if (unsub) unsub();
      window.removeEventListener('abrirComentariosPerfil', escutarOrdemDeAbrirPost);
    };
  }, [userDados?.uid, setIdPostAtivo]);

const [seguindo, setSeguindo] = React.useState(false);
const [bloqueado, setBloqueado] = React.useState(false);
const [totalSeguidores, setTotalSeguidores] = React.useState(0);
const [totalSeguindo, setTotalSeguindo] = React.useState(0);
const [listaSeguidores, setListaSeguidores] = React.useState([]);
const [mostrarModalSeguidores, setMostrarModalSeguidores] = React.useState(false);
const [listaSeguindo, setListaSeguindo] = React.useState([]);
const [mostrarModalSeguindo, setMostrarModalSeguindo] = React.useState(false);
// Number(...) com rede: se o Firestore devolver o saldo como texto (aconteceu
// em fichas antigas), `.toFixed`/`.toLocaleString` atiravam um TypeError a meio
// do render — e um erro no render desmonta a árvore toda, deixando ecrã preto.
const paraNumero = (valor) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};
const [minhasBolas, setMinhasBolas] = React.useState(paraNumero(userDados?.saldoBolas));
const [mostrarModalBloqueados, setMostrarModalBloqueados] = React.useState(false);
const [listaBloqueados, setListaBloqueados] = React.useState([]);
const [carregandoFoto, setCarregandoFoto] = React.useState(false);
const [fotoAtual, setFotoAtual] = React.useState(userDados?.fotoUrl || auth.currentUser?.photoURL);
const [mostrarListaLikes, setMostrarListaLikes] = useState(false);

const [infoAberta, setInfoAberta] = React.useState(false);

  // 🔍 ESTADOS AUXILIARES PARA FILTRAR AS LUPAS DE PESQUISA (ANTI-CORTES)
  const [termoPesquisaSeguidores, setTermoPesquisaSeguidores] = useState("");
  const [termoPesquisaSeguindo, setTermoPesquisaSeguindo] = useState("");
  const [termoPesquisaBloqueados, setTermoPesquisaBloqueados] = useState("");

    // 🔍 MATEMÁTICA DE FILTRAGEM DIRETA PARA AS TUAS LISTAS ORIGINAIS
  const seguidoresFiltrados = listaSeguidores.filter(s => (s.nome || "").toLowerCase().includes(termoPesquisaSeguidores.toLowerCase()));
  const seguindoFiltrados = listaSeguindo.filter(s => (s.nome || "").toLowerCase().includes(termoPesquisaSeguindo.toLowerCase()));
  const bloqueadosFiltrados = listaBloqueados.filter(b => (b.nome || "").toLowerCase().includes(termoPesquisaBloqueados.toLowerCase()));

const mudarFotoPerfil = async (event) => {
  const file = event.target.files[0];
  if (!file || userDados.uid !== auth.currentUser?.uid) return;

  setCarregandoFoto(true);
  try {
    const fotoRef = ref(storage, `perfis/${userDados.uid}`);
    await uploadBytes(fotoRef, file);
    const url = await getDownloadURL(fotoRef);

    // Atualiza no Firebase Auth
    await updateProfile(auth.currentUser, { photoURL: url });

    // Atualiza no Firestore
    const { updateDoc, doc } = await import('firebase/firestore');
    await updateDoc(doc(db, "utilizadores", userDados.uid), { fotoUrl: url });

    setFotoAtual(url);
    alert("Foto de perfil updated! ⚽");
  } catch (e) {
    alert("Erro ao mudar foto.");
  }
  setCarregandoFoto(false);
};

  React.useEffect(() => {
    // Guarda-chuva de listeners. Sem isto, os quatro onSnapshot abaixo NUNCA
    // eram desligados: cada abertura do perfil (e o StrictMode monta duas
    // vezes) deixava mais um canal aberto contra o Firestore. Com o
    // `experimentalForceLongPolling` do firebase.js cada canal é uma ligação
    // HTTP presa — no iPhone isso esgota a quota de ligações do Safari e o
    // ecrã fica à espera de dados que já não chegam.
    let vivo = true;
    const desligar = [];

    // O nome da funcao dizia "tempo real" mas era um `getDoc` de leitura unica:
    // mudar o saldo na consola do Firebase nao aparecia aqui sem recarregar a
    // pagina. Passa a ser um escutador a serio.
    if (userDados?.uid) {
      desligar.push(observarSaldo(userDados.uid, ({ bolas, dados }) => {
        if (!vivo) return;
        setMinhasBolas(bolas); // 🌟 Mantém apenas o monitor de pontuação desportiva seguro
        if (dados.fotoUrl) setFotoAtual(dados.fotoUrl);
        else if (dados.photoURL) setFotoAtual(dados.photoURL);
      }));
    }

    const carregarEstadosReais = async () => {
      const meuId = auth.currentUser?.uid;
      const idDeles = userDados?.uid;
      if (!meuId || !idDeles) return;

      try {
        const { getDoc, doc, collection, onSnapshot } = await import('firebase/firestore');
        if (!vivo) return;

        // 1. Verifica se já sigo este utilizador
        const sigoSnap = await getDoc(doc(db, "utilizadores", meuId, "seguindo", idDeles));
        if (vivo && sigoSnap.exists()) setSeguindo(true);

        // 2. Verifica se já bloqueei este utilizador
        const bloqueioSnap = await getDoc(doc(db, "utilizadores", meuId, "bloqueados", idDeles));
        if (vivo && bloqueioSnap.exists()) setBloqueado(true);

        if (!vivo) return;
                        // 3, 4, 5 e 6. Contadores e Listas em Tempo Real

            // Esta parte trata de quem segue este utilizador
    desligar.push(onSnapshot(collection(db, "utilizadores", idDeles, "seguidores"), (snap) => {
      setTotalSeguidores(snap.size);
      setListaSeguidores(snap.docs.map(d => ({ 
        uid: d.id, 
        nome: d.data().nome || "Craque",
        fotoUrl: d.data().fotoUrl || "" // Puxa a foto guardada
      })));
    }, (e) => console.error("Erro a ouvir seguidores:", e)));

    // Esta parte trata de quem este utilizador segue
    desligar.push(onSnapshot(collection(db, "utilizadores", idDeles, "seguindo"), (snap) => {
      setTotalSeguindo(snap.size);
      setListaSeguindo(snap.docs.map(d => ({ 
        uid: d.id, 
        nome: d.data().nome || "Craque",
        fotoUrl: d.data().fotoUrl || "" // Puxa a foto guardada
      })));
    }, (e) => console.error("Erro a ouvir quem segue:", e)));

                        // 7. Lista de Bloqueados em Tempo Real
        desligar.push(onSnapshot(collection(db, "utilizadores", meuId, "bloqueados"), (snap) => {
          setListaBloqueados(snap.docs.map(docBlock => ({
            uid: docBlock.id,
            nome: docBlock.data().nome || "Utilizador"
          })));
        }, (e) => console.error("Erro a ouvir bloqueados:", e)));

        // 8. Verifica se eu estou bloqueado por ele
        desligar.push(onSnapshot(doc(db, "utilizadores", idDeles, "bloqueados", meuId), (snap) => {
          if (snap.exists()) setBloqueado(true);
        }, (e) => console.error("Erro a ouvir bloqueio mútuo:", e)));

      } catch (e) {
        console.error("Erro ao carregar memória:", e);
      }
    };

    carregarEstadosReais();

    return () => {
      vivo = false;
      desligar.forEach((parar) => { try { parar(); } catch { /* já estava desligado */ } });
    };
  }, [userDados?.uid]);

  // Sem dados não há perfil para desenhar. Pode ser que ainda estejam a
  // carregar, ou que a conta esteja autenticada sem ficha em `utilizadores`
  // (aconteceu a contas antigas e a registos que falharam a meio).
  if (!userDados?.uid) {
    const aindaACarregar = !!auth.currentUser;
    return (
      <div className="perfil-ecra" style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: '#000', color: '#39FF14',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: '14px', padding: '30px',
        textAlign: 'center', zIndex: 50, boxSizing: 'border-box',
      }}>
        <div style={{ fontSize: '34px' }}>{aindaACarregar ? '⚽' : '🔒'}</div>
        <p style={{ fontWeight: 900, fontSize: '14px', margin: 0 }}>
          {aindaACarregar ? 'A CARREGAR O PERFIL...' : 'PRECISAS DE ENTRAR PRIMEIRO'}
        </p>
        {!aindaACarregar && (
          <button
            onClick={() => { window.location.replace('/'); }}
            style={{
              background: '#39FF14', color: '#000', border: 'none', borderRadius: '25px',
              padding: '12px 28px', fontWeight: 900, fontSize: '13px', cursor: 'pointer',
            }}
          >
            IR PARA O LOGIN ⚽
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="perfil-ecra" style={{ 
      // top/left/right/bottom em vez de width/height a 100%: o contentor passa a
      // ser medido pela janela real. Escrito assim, e não com o atalho `inset`,
      // porque o Safari do iPhone só conhece `inset` a partir do iOS 14.5 — nas
      // versões anteriores a regra era ignorada e a caixa ficava a zero de
      // altura, ou seja, invisível sobre o fundo preto.
      //
      // 🎨 NOVO FUNDO PREMIUM COM A TUA ARTE DO ESTÁDIO (ESTILO ALII)
      background: `url(${imgFundoPerfil}) center/cover no-repeat`,
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      // Acima do feed, abaixo da .bottom-nav (z-index 100) para o menu continuar
      // clicável. Estava em 10, o mesmo patamar de vários cartões do feed.
      zIndex: 50,
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      // O ecrã do perfil é mais alto do que um iPhone. Sem isto o conteúdo
      // ficava cortado a meio e não havia forma de chegar às grelhas de
      // vídeos/fotos — no iPhone SE nem os separadores apareciam.
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      overscrollBehaviorY: 'contain',
      // Sem border-box, o paddingTop somava-se aos 100% e o contentor passava a
      // ser 100px mais alto do que a janela.
      boxSizing: 'border-box',
      paddingTop: 'calc(100px + env(safe-area-inset-top, 0px))',
      // 70px da .bottom-nav + folga + o indicador de início do iPhone.
      paddingBottom: 'calc(110px + env(safe-area-inset-bottom, 0px))'
    }}>

    {/* CENTRAL TÁTICA DE COMPETIÇÕES E PERFIL (ALINHADA COM A BIO - INTEGRADA NA PONTA) */}
      <div style={{ 
        display: 'flex', 
        width: '92%', 
        maxWidth: '340px', 
        justifyContent: userDados?.uid === auth.currentUser?.uid ? 'space-between' : 'center', 
        alignItems: 'center', 
        marginTop: '-35px',
        marginBottom: '10px',
        zIndex: 20,
        gap: '12px'
      }}>

            {/* BLOCO DA FOTO, BOTÃO SAIR E NÍVEIS (TUDO À ESQUERDA) */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          
          {/* FOTO E BOTAO SAIR */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <label htmlFor="mudar-foto-perfil" style={{ cursor: 'pointer', margin: 0, padding: 0 }}>
              <div className="photo-circle" style={{ 
                width: '100px', 
                height: '100px', 
                backgroundImage: `url(${fotoAtual || userDados.fotoUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '4px solid #39FF14',
                boxShadow: '0 0 20px rgba(57, 255, 20, 0.4)',
                position: 'relative',
                opacity: carregandoFoto ? 0.5 : 1
              }}>
                {/* ÍCONE DE EDITAR */}
                {userDados.uid === auth.currentUser?.uid && (
                  <div style={{
                    position: 'absolute', bottom: '2px', right: '-4px', 
                    background: '#39FF14', borderRadius: '50%', 
                    width: '26px', height: '26px', display: 'flex', 
                    justifyContent: 'center', alignItems: 'center', color: 'black',
                    fontSize: '14px', fontWeight: 'bold', border: '2px solid black',
                    zIndex: 26
                  }}>
                    {carregandoFoto ? "..." : "✎"}
                  </div>
                )}
              </div>
              
              {userDados.uid === auth.currentUser?.uid && (
                <input 
                  type="file" 
                  id="mudar-foto-perfil" 
                  accept="image/*" 
                  onChange={mudarFotoPerfil} 
                  hidden 
                />
              )}
            </label>

            {/* BOTÃO SAIR */}
            {userDados?.uid === auth.currentUser?.uid && (
              <button
                onClick={() => {
                  if (window.confirm("Queres mesmo sair da tua conta? ⚽")) {
                    signOut(auth).then(() => {
                      window.location.reload();
                    });
                  }
                }}
                style={{
                  marginTop: '8px',
                  background: '#ff4444',
                  color: 'black',
                  border: '1.5px solid black',
                  borderRadius: '8px',
                  padding: '4px 12px',
                  fontSize: '10px',
                  fontWeight: '950',
                  cursor: 'pointer',
                  boxShadow: '1.5px 1.5px 0px black'
                }}
              >
                SAIR
              </button>
            )}
          </div>

                {/* OS DOIS NÍVEIS EMPILHADOS (COLADOS À DIREITA DA FOTO) */}
          {userDados?.uid === auth.currentUser?.uid && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              
                            {/* NÍVEL JOGADOR */}
              <div 
                onClick={() => setModalJogadorAberta(true)}
                style={{
                  cursor: 'pointer', border: '2px solid #39FF14', width: '54px', height: '54px',
                  borderRadius: '50%', display: 'flex', flexDirection: 'column', 
                  alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)',
                  boxShadow: '0 0 10px rgba(57,255,20,0.2)', boxSizing: 'border-box',
                  overflow: 'hidden', position: 'relative'
                }}
              >
                <img 
                  src={obterImagemJogador(nivelJogador)} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  alt="Jogador" 
                />
                <span style={{ 
                  position: 'absolute', bottom: '2px', fontSize: '7px', fontWeight: '950', 
                  background: 'rgba(0,0,0,0.7)', padding: '0 4px', borderRadius: '4px', color: '#39FF14' 
                }}>
                  LV. {nivelJogador}
                </span>

                {/* JANELA DO JOGADOR TRANCADA AQUI DENTRO SEM ERROS */}
                <ModalJogador aberta={modalJogadorAberta} aoFechar={(e) => { e.stopPropagation(); setModalJogadorAberta(false); }} nivel={nivelJogador} xp={userDados?.xpJogador || 0} />
              </div>

                          {/* NÍVEL ADEPTO */}
              <div 
                onClick={() => setModalAdeptoAberta(true)}
                style={{
                  cursor: 'pointer', border: '2px solid #FF007F', width: '54px', height: '54px',
                  borderRadius: '50%', display: 'flex', flexDirection: 'column', 
                  alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)',
                  boxShadow: '0 0 10px rgba(255,0,127,0.2)', boxSizing: 'border-box',
                  overflow: 'hidden', position: 'relative'
                }}
              >
                <img 
                  src={obterImagemAdepto(nivelAdepto)} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  alt="Adepto" 
                />
                <span style={{ 
                  position: 'absolute', bottom: '2px', fontSize: '7px', fontWeight: '950', 
                  background: 'rgba(0,0,0,0.7)', padding: '0 4px', borderRadius: '4px', color: '#FF007F' 
                }}>
                  LV. {nivelAdepto}
                </span>

                {/* JANELA DO ADEPTO TRANCADA AQUI DENTRO SEM ERROS */}
                <ModalAdepto aberta={modalAdeptoAberta} aoFechar={(e) => { e.stopPropagation(); setModalAdeptoAberta(false); }} nivel={nivelAdepto} xp={userDados?.xpAdepto || 0} />
              </div>
            
            <InfoNiveis isOpen={infoAberta} onClose={() => setInfoAberta(false)} />

            </div>
          )}
        </div>

      {/* GRELHA DE COMPETIÇÕES 2X2 (DIREITA - ALARGADA E COM NOMES COMPLETOS SEM ABREVIATURAS) */}
        {userDados?.uid === auth.currentUser?.uid && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '10px 8px', 
            flex: 1,
            justifyItems: 'center'
          }}>
            {/* 1. LIVE */}
            <div 
              onClick={() => {
                window.dispatchEvent(new CustomEvent('abrirLive', { 
                  detail: { uid: auth?.currentUser?.uid, donoNome: auth?.currentUser?.displayName } 
                }));
              }}
              style={{
                cursor: 'pointer', border: '2px solid #ffffff', width: '54px', height: '54px',
                borderRadius: '50%', color: '#ffffff', display: 'flex', flexDirection: 'column', 
                alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)',
                boxShadow: '0 0 10px rgba(255,255,255,0.2)', boxSizing: 'border-box'
              }}
            >
              <span style={{ fontSize: '14px', marginBottom: '-2px' }}>🎥</span>
              <span style={{ fontSize: '7.5px', fontWeight: '950', letterSpacing: '0.2px' }}>LIVE</span>
            </div>

            {/* 2. ARENA PK */}
            <div 
              onClick={(e) => {
                e.stopPropagation();
                window.location.search = '?arena';
              }}
              style={{
                cursor: 'pointer', border: '2px solid #39FF14', width: '54px', height: '54px',
                borderRadius: '50%', color: '#39FF14', display: 'flex', flexDirection: 'column', 
                alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)',
                boxShadow: '0 0 10px rgba(57,255,20,0.2)', textShadow: '0 0 4px #39FF14', boxSizing: 'border-box'
              }}
            >
              <span style={{ fontSize: '13px', marginBottom: '-2px' }}>⚔️</span>
              <span style={{ fontSize: '7.5px', fontWeight: '950', textAlign: 'center', lineHeight: '1' }}>ARENA PK</span>
            </div>

            {/* 3. CHAMPIONS PK */}
            <div 
              onClick={(e) => {
                e.stopPropagation();
                window.location.search = '?champions';
              }}
              style={{
                cursor: 'pointer', border: '2px solid #FFD700', width: '54px', height: '54px',
                borderRadius: '50%', color: '#FFD700', display: 'flex', flexDirection: 'column', 
                alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)',
                boxShadow: '0 0 10px rgba(255,215,0,0.2)', textShadow: '0 0 4px #FFD700', boxSizing: 'border-box'
              }}
            >
              <span style={{ fontSize: '13px', marginBottom: '-2px' }}>🏆</span>
              <span style={{ fontSize: '6.5px', fontWeight: '950', textAlign: 'center', lineHeight: '1', padding: '0 2px' }}>CHAMPIONS</span>
            </div>

            {/* 4. LIGA PK */}
            <div 
              onClick={(e) => {
                e.stopPropagation();
                window.location.search = '?ligapk';
              }}
              style={{
                cursor: 'pointer', border: '2px solid #00E5FF', width: '54px', height: '54px',
                borderRadius: '50%', color: '#00E5FF', display: 'flex', flexDirection: 'column', 
                alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)',
                boxShadow: '0 0 12px #00E5FF', textShadow: '0 0 4px #00E5FF', boxSizing: 'border-box'
              }}
            >
              <span style={{ fontSize: '13px', marginBottom: '-2px' }}>🏟️</span>
              <span style={{ fontSize: '7.5px', fontWeight: '950', textAlign: 'center', lineHeight: '1' }}>LIGA PK</span>
            </div>
          </div>
        )}
      </div>

      {/* INTERFACE DE NOME/NICKNAME EDITÁVEL EM TEMPO REAL (LIBERTO PARA O UTILIZADOR) */}
      {(() => {
        // Estado local temporário controlado via window para evitar loops ou re-renders no ficheiro principal
        const [estaEditandoNome, setEstaEditandoNome] = React.useState(false);
        const [nomeTemporario, setNomeTemporario] = React.useState(userDados.nomeCompleto || "");

        const salvarNomeNicknameNovo = async () => {
          const meuIdAtual = auth.currentUser?.uid;
          if (!meuIdAtual || !nomeTemporario.trim()) return;
          try {
            const { doc: fDoc, updateDoc: fUpdateDoc } = await import('firebase/firestore');
            await fUpdateDoc(fDoc(db, "utilizadores", meuIdAtual), {
              nomeCompleto: nomeTemporario.trim()
            });
            setEstaEditandoNome(false);
            alert("Nome / Nickname atualizado com sucesso! ⚽");
            window.location.reload(); // Recarrega para sincronizar os estados e o feed global
          } catch (errNome) {
            console.error("Erro ao salvar novo nome/nickname:", errNome);
            alert("Não foi possível atualizar o nome. Tenta outra vez!");
          }
        };

        if (estaEditandoNome && userDados?.uid === auth.currentUser?.uid) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <input 
                type="text"
                value={nomeTemporario}
                onChange={(e) => setNomeTemporario(e.target.value)}
                maxLength={25}
                style={{
                  backgroundColor: '#111',
                  color: '#fff',
                  border: '2px solid #39FF14',
                  borderRadius: '10px',
                  padding: '4px 10px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  outline: 'none',
                  textTransform: 'uppercase',
                  width: '160px'
                }}
              />
              <button 
                onClick={salvarNomeNicknameNovo}
                style={{ backgroundColor: '#39FF14', color: 'black', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '11px', fontWeight: '950', cursor: 'pointer' }}
              >
                OK
              </button>
              <button 
                onClick={() => setEstaEditandoNome(false)}
                style={{ backgroundColor: 'transparent', color: '#ff4444', border: 'none', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
          );
        }

        return (
          <h2 
            onClick={() => {
              if (userDados?.uid === auth.currentUser?.uid) {
                setEstaEditandoNome(true);
              }
            }}
            style={{ 
              color: '#ffffff', 
              marginTop: '4px', 
              fontSize: '22px', 
              fontWeight: 'bold',
              textTransform: 'uppercase',
              cursor: userDados?.uid === auth.currentUser?.uid ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            title={userDados?.uid === auth.currentUser?.uid ? "Clica para editar o teu nome ou nickname" : ""}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
            {userDados.nomeCompleto}
            {userDados?.uid === auth.currentUser?.uid && (
              <span style={{ fontSize: '12px', color: '#39FF14', opacity: 0.7 }}>✎</span>
            )}
              <span style={{ color: '#aaa', fontSize: '12px', fontWeight: 'normal', marginTop: '2px', textTransform: 'none' }}>
            ID: {userDados?.uid}
            </span>
            </div>
            </h2>
            );
            })()}
       <p style={{ color: '#39FF14', fontSize: '12px', marginTop: '-4px', fontWeight: 'bold' }}>
        MAIOR DE 18 ANOS • ACESSO RESTRITO 🔞
      </p>

    {/* BLOCO SOCIAL FINAL - SEGUIR/DESEGUIR E BLOQUEAR/DESBLOQUEAR (COM SIMETRIA PERFEITA) */}
      {/* CONTADORES DE SEGUIDORES E SEGUINDO */}
      <div style={{ display: 'flex', gap: '40px', margin: '15px 0', justifyContent: 'center', width: '100%' }}>
        <div 
          onClick={() => setMostrarModalSeguidores(true)} 
          style={{ textAlign: 'center', cursor: 'pointer' }}
        >
          <span style={{ color: 'white', fontWeight: 'bold', fontSize: '18px', display: 'block' }}>{totalSeguidores}</span>
          <small style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', fontWeight: '600' }}>Seguidores</small>
        </div>
        
        <div 
          onClick={() => setMostrarModalSeguindo(true)} 
          style={{ textAlign: 'center', cursor: 'pointer' }}
        >
          <span style={{ color: 'white', fontWeight: 'bold', fontSize: '18px', display: 'block' }}>{totalSeguindo}</span>
          <small style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', fontWeight: '600' }}>A Seguir</small>
        </div>
        
        {userDados.uid === auth.currentUser?.uid && (
          <div 
            onClick={() => setMostrarModalBloqueados(true)} 
            style={{ textAlign: 'center', cursor: 'pointer' }}
          >
            <span style={{ color: '#ff4444', fontWeight: 'bold', fontSize: '18px', display: 'block' }}>🚫</span>
            <small style={{ color: '#888', fontSize: '10px', textTransform: 'uppercase', fontWeight: '600' }}>Bloqueados</small>
          </div>
        )}
      </div>

      {userDados.uid !== auth.currentUser?.uid && (
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px', marginBottom: '10px' }}>
          <button 
            onClick={async () => {
              const meuId = auth.currentUser?.uid;
              const idDeles = userDados?.uid;
              alert("Vou seguir o ID: " + idDeles);
              const meuNome = auth.currentUser?.displayName || "Craque";
              
              if (!meuId || !idDeles) return;

              const novoEstadoSeguir = !seguindo;
              setSeguindo(novoEstadoSeguir);

              try {
                const { serverTimestamp, doc, setDoc, deleteDoc } = await import('firebase/firestore');

                if (novoEstadoSeguir) {
                  await setDoc(doc(db, "utilizadores", meuId, "seguindo", idDeles), {
                    nome: userDados.nomeCompleto,
                    data: serverTimestamp()
                  });
                  await setDoc(doc(db, "utilizadores", idDeles, "seguidores", meuId), {
                    nome: meuNome,
                    data: serverTimestamp()
                  });

                  setTotalSeguidores(prev => prev + 1);
                  enviarNotificacao(idDeles, meuId, meuNome, "seguidor");
                } else {
                  await deleteDoc(doc(db, "utilizadores", meuId, "seguindo", idDeles));
                  await deleteDoc(doc(db, "utilizadores", idDeles, "seguidores", meuId));
                  setTotalSeguidores(prev => Math.max(0, prev - 1));
                }
              } catch (e) {
                console.error("Erro ao seguir:", e);
                setSeguindo(!novoEstadoSeguir);
              }
            }}
            style={{
              background: seguindo ? 'transparent' : '#39FF14',
              color: seguindo ? '#39FF14' : 'black',
              border: seguindo ? '1px solid #39FF14' : 'none',
              padding: '8px 20px', borderRadius: '20px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer'
            }}
          >
            {seguindo ? 'A SEGUIR' : 'SEGUIR'}
          </button>
          
          <button 
            onClick={async () => {
              const meuId = auth.currentUser?.uid;
              const idDeles = userDados?.uid;
              if (!meuId || !idDeles) return;

              try {
                const { serverTimestamp, doc, setDoc, deleteDoc } = await import('firebase/firestore');

                if (!bloqueado) {
                  if (window.confirm("Queres mesmo bloquear este utilizador? 🚫")) {
                    await setDoc(doc(db, "utilizadores", meuId, "bloqueados", idDeles), {
                      nome: userDados.nomeCompleto,
                      data: serverTimestamp()
                    });

                    await deleteDoc(doc(db, "utilizadores", meuId, "seguindo", idDeles));
                    await deleteDoc(doc(db, "utilizadores", idDeles, "seguidores", meuId));
                    await deleteDoc(doc(db, "utilizadores", idDeles, "seguindo", meuId));
                    await deleteDoc(doc(db, "utilizadores", meuId, "seguidores", idDeles));

                    setBloqueado(true);
                    setTotalSeguidores(0); 
                    
                    alert("Utilizador bloqueado! As vossas ligações foram removidas.");
                    window.location.reload(); 
                  }
                } else {
                  if (window.confirm("Queres desbloquear este utilizador? ✅")) {
                    await deleteDoc(doc(db, "utilizadores", meuId, "bloqueados", idDeles));
                    setBloqueado(false);
                    alert("Utilizador desbloqueado!");
                  }
                }
              } catch (e) { 
                console.error("Erro no bloqueio:", e); 
              }
            }}
            style={{
              background: bloqueado ? '#555' : 'transparent',
              color: bloqueado ? '#fff' : '#ff4444',
              border: bloqueado ? 'none' : '1px solid #ff4444',
              padding: '8px 15px', borderRadius: '20px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer'
            }}
          >
            {bloqueado ? 'BLOQUEADO' : 'BLOQUEAR'}
          </button>
        </div>
      )}

    {/* 3. BIO EDITÁVEL COM TRANCA DE SEGURANÇA INTEGRADA NO VIDRO */}
      <div style={{ 
        marginTop: '8px', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        background: 'rgba(255, 255, 255, 0.04)', 
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        padding: '0 12px',
        borderRadius: '15px',
        border: '1px solid rgba(255, 255, 255, 0.12)', 
        width: '85%',
        maxWidth: '280px',
        height: '40px',
        boxSizing: 'border-box'
      }}>
        <textarea 
          id="campo-bio"
          placeholder="Escreve algo sobre ti..."
          defaultValue={userDados?.bio || ""}
          readOnly={userDados.uid !== auth.currentUser?.uid} // TRANCA O TEXTO
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '13px',
            width: '100%',
            resize: 'none',
            fontFamily: 'inherit',
            outline: 'none',
            lineHeight: '38px',
            height: '38px',
            overflow: 'hidden'
          }}
          maxLength={40}
        />
        
        {/* O BOTÃO SÓ APARECE SE O PERFIL FOR O TEU */}
        {userDados.uid === auth.currentUser?.uid && (
          <button 
            id="btn-bio"
            onClick={async () => {
              const btn = document.getElementById('btn-bio');
              const campo = document.getElementById('campo-bio');
              
              if (btn.innerText === "OK") {
                try {
                  const { doc, updateDoc } = await import('firebase/firestore');
                  await updateDoc(doc(db, "utilizadores", userDados.uid), { bio: campo.value });
                  btn.innerText = "✎"; 
                  btn.style.background = "#555"; 
                  campo.readOnly = true; // TRANCA O CAMPO
                  alert("Bio atualizada! ⚽");
                } catch (e) { alert("Erro!"); }
              } else {
                btn.innerText = "OK"; 
                btn.style.background = "#39FF14";
                campo.readOnly = false; // DESTRANCA PARA ESCREVER
                campo.focus();
              }
            }}
            style={{
              background: '#555', color: 'black', border: 'none',
              borderRadius: '50%', width: '24px', height: '24px',
              fontWeight: 'bold', fontSize: '11px', flexShrink: 0,
              transition: '0.3s', cursor: 'pointer'
            }}
          >
            ✎
          </button>
        )}
      </div>

      {/* 2. EXIBIÇÃO DE SALDOS E ACÇÕES OTIMIZADA EM LINHA HORIZONTAL */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '12px', 
              width: '100%', 
              maxWidth: '320px',
              margin: '12px auto 14px auto',
              padding: '0 4px',
              boxSizing: 'border-box'
            }}>
              
              {/* BOTÃO CARREGAR (ESQUERDA) - SÓ EXIBE NO PRÓPRIO PERFIL */}
              {userDadosProp?.uid === auth.currentUser?.uid && (
                <button 
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('abrirMenuSaldo'))}
                  style={{ 
                    flex: 1, 
                    padding: '10px 6px', 
                    borderRadius: '20px', 
                    border: '1px solid #39FF14', 
                    background: 'linear-gradient(135deg, rgba(57, 255, 20, 0.15) 0%, rgba(0, 0, 0, 0.6) 100%)', 
                    color: '#39FF14', 
                    fontSize: '11px', 
                    cursor: 'pointer', 
                    fontWeight: '900',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  ➕ Carregar
                </button>
              )}

              {/* CAIXA DE VISUALIZAÇÃO DO SALDO CENTRADA (MEIO) */}
              <div style={{ 
                flex: userDadosProp?.uid === auth.currentUser?.uid ? 1.1 : 1, 
                width: '100%',
                background: 'rgba(255, 255, 255, 0.04)', 
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                padding: '8px 6px', 
                borderRadius: '16px', 
                border: '1px solid rgba(255, 255, 255, 0.08)', 
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                boxSizing: 'border-box'
              }}>
                <span style={{ color: '#39FF14', fontSize: '15px', fontWeight: '950', display: 'block', fontFamily: 'monospace', textShadow: '0 0 6px rgba(57,255,20,0.3)' }}>
                  ⚽ {minhasBolas.toLocaleString()}
                </span>
                <small style={{ color: '#888', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 'bold', display: 'block', marginTop: '1px' }}>BOLAS</small>
              </div>

              {/* BOTÃO CARTEIRA (DIREITA) - SÓ EXIBE NO PRÓPRIO PERFIL */}
              {userDadosProp?.uid === auth.currentUser?.uid && (
                <button 
                  type="button"
                  onClick={() => setEcraAtivo('carteira')}
                  style={{ 
                    flex: 1, 
                    padding: '10px 6px', 
                    borderRadius: '20px', 
                    border: '1px solid #FFD700', 
                    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(0, 0, 0, 0.6) 100%)', 
                    color: '#FFD700', 
                    fontSize: '11px', 
                    cursor: 'pointer', 
                    fontWeight: '900',
                    boxShadow: '0 0 8px rgba(255, 215, 0, 0.15)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  💼 Carteira
                </button>
              )}

            </div>

          {/* BOTÃO MESTRE DA CADERNETA DE CROMOS PREMIUM E COMPACTO */}
        {userDados.uid === auth.currentUser?.uid && (
          <button
            onClick={() => {
              // Dispara um alarme global que o Feed.jsx vai ouvir para abrir a caderneta na hora
              window.dispatchEvent(new CustomEvent('mudarEcraGlobal', { detail: 'caderneta_galeria' }));
            }}
            style={{
              marginTop: '10px', // Reduzido de 15px para poupar espaço vertical e subir as publicações
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              color: 'black',
              border: '1.5px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px', // Cantos mais ovais e modernos para combinar com os botões redondos
              padding: '8px 20px', // Reduzido para libertar o elevador de posts para o topo
              fontSize: '12px',
              fontWeight: '950',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3), inset 0 1px 2px rgba(255,255,255,0.4)',
              letterSpacing: '0.5px',
              width: '85%',
              maxWidth: '280px',
              textTransform: 'uppercase',
              transition: 'transform 0.1s ease'
            }}
          >
            Álbum de Cadernetas 🏟️🎨
          </button>
        )}
      
        {/* 📊 GRELHA DE CONTEÚDO SEPARADA EM VIDRO (PUXA OS POSTS PARA O TOPO DE IMEDIATO) */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-around', 
        padding: '10px 0', 
        background: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.06)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        width: '100%',
        marginTop: '6px' // Encolheu para colar o elevador de posts ao cabeçalho
      }}>
        {/* SECÇÃO VÍDEOS */}
        <button
          onClick={() => setAbaGrelha('videos')}
          style={{ background: 'none', border: 'none', color: abaGrelha === 'videos' ? '#39FF14' : 'rgba(255,255,255,0.6)', fontWeight: '900', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', transition: 'all 0.2s ease', letterSpacing: '0.5px' }}
        >
          <PlayCircle size={16} color={abaGrelha === 'videos' ? "#39FF14" : "rgba(255,255,255,0.6)"} />
          VÍDEOS
        </button>

        {/* SECÇÃO FOTOS */}
        <button
          onClick={() => setAbaGrelha('fotos')}
          style={{ background: 'none', border: 'none', color: abaGrelha === 'fotos' ? '#39FF14' : 'rgba(255,255,255,0.6)', fontWeight: '900', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', transition: 'all 0.2s ease', letterSpacing: '0.5px' }}
        >
          FOTOS
          <Camera size={16} color={abaGrelha === 'fotos' ? "#39FF14" : "rgba(255,255,255,0.6)"} />
        </button>
      </div>

      {/* GRELHA MATRIZ DINÂMICA COMPACTA - 3 PRIMEIROS VISÍVEIS LOGO À ENTRADA */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: '2px', 
        width: '100%', 
        marginTop: '6px', // Reduzido drasticamente para eliminar o espaço em branco do topo
        flexShrink: 0,
        background: 'transparent' // Deixa a imagem linda do teu estádio passar por trás das divisórias dos posts
      }}>
        {meusPosts
          .filter(p => p.tipo === (abaGrelha === 'videos' ? 'video' : 'foto'))
          .map((post) => (
            <div 
              key={post.id} 
              onClick={() => setPostSelecionado(post)} 
              style={{ 
                aspectRatio: '1/1', 
                background: 'rgba(255, 255, 255, 0.02)', 
                border: '1px solid rgba(255, 255, 255, 0.04)',
                position: 'relative', 
                cursor: 'pointer',
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}
            >
              {post.tipo === 'video' ? (
                <video src={post.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <img src={post.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              )}
            </div>
          ))}
      </div>
        
                    {/* VISUALIZADOR TESTE DE SOBREVIVÊNCIA */}
      {postSelecionado && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: '#000', zIndex: 100000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          
          {/* BOTÃO X */}
          <button 
            onClick={() => setPostSelecionado(null)}
            style={{ position: 'absolute', top: '45px', left: '20px', zIndex: 100001, background: '#39FF14', color: '#000', border: 'none', borderRadius: '50%', width: '25px', height: '25px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}
          >X</button>

                    {/* BOTÃO APAGAR REAL */}
          {postSelecionado.userId === auth.currentUser?.uid && (          
          <button 
            onClick={async () => {
              if (window.confirm("Queres mesmo eliminar esta publicação? 🗑️")) {
                try {
                  // Importamos as ferramentas aqui dentro para não dar erro no topo
                  const { doc, deleteDoc } = await import('firebase/firestore');
                  const { db } = await import('./firebase'); // Garante que o caminho do teu ficheiro firebase está certo
                  
                  await deleteDoc(doc(db, "posts", postSelecionado.id));
                  setPostSelecionado(null); // Fecha o visualizador
                  alert("Publicação eliminada! ⚽");
                } catch (erro) {
                  console.error(erro);
                  alert("Erro ao eliminar. Tenta novamente.");
                }
              }
            }}
            style={{ 
              position: 'absolute', top: '45px', left: '55px', 
              zIndex: 100001, background: '#ff4444', color: 'white', 
              border: 'none', borderRadius: '5px', padding: '5px 10px', 
              fontWeight: 'bold', fontSize: '10px', height: '25px', cursor: 'pointer' 
            }}
          >APAGAR</button>
          )}

                              {/* BARRA LATERAL DE INTERAÇÃO (PERFIL) */}
          <div style={{ 
            position: 'absolute', 
            bottom: '150px', 
            right: '20px', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '25px', 
            zIndex: 100003, 
            alignItems: 'center' 
          }}>
            
            {/* 1. CORAÇÃO (Invisível para o dono) */}
                            <div 
          onClick={async () => {
            if (postSelecionado.userId === auth.currentUser?.uid) {
              // GATILHO PARA ABRIR A LISTA
              setMostrarListaLikes(true);
            } else {
              // LÓGICA DE DAR LIKE (VISITANTE)
              const listaAtual = postSelecionado.quemDeuLike || [];
              const meuId = auth.currentUser?.uid;
              const jaDeuLike = listaAtual.includes(meuId);
              
              const novaLista = jaDeuLike 
                ? listaAtual.filter(uid => uid !== meuId)
                : [...listaAtual, meuId];
              
              const novoTotal = novaLista.length;

              setPostSelecionado({ ...postSelecionado, likes: novoTotal, quemDeuLike: novaLista });
              await darLike(postSelecionado.id, novoTotal);
            }
          }}
          style={{ cursor: 'pointer', textAlign: 'center' }}
        >
          <div style={{ fontSize: '32px' }}>❤️</div>
          <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
            {postSelecionado.likes || 0}
          </span>
        </div>

            {/* 2. BOTÃO DE COMENTÁRIOS (Visível para todos) */}
            <div 
              onClick={() => window.dispatchEvent(new CustomEvent('abrirComentariosPerfil', { detail: postSelecionado }))}
              style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MessageCircle size={32} color="white" />
                <span style={{ 
                  position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%, -50%)', 
                  color: '#39FF14', fontSize: '10px', fontWeight: '900', textShadow: '0 0 5px #39FF14' 
                }}>
                  {postSelecionado.totalComentarios > 99 ? '+99' : (postSelecionado.totalComentarios || 0)}
                </span>
              </div>
              <span style={{ color: 'white', fontSize: '9px', fontWeight: 'bold', marginTop: '4px' }}>COMENTAR</span>
            </div>

            {/* 3. SETA DE PARTILHAR (Visível para todos) */}
            <div 
              onClick={async () => {
                const linkReal = window.location.origin + "/?post=" + postSelecionado.id;
                if (navigator.share) {
                  try {
                    await navigator.share({ title: 'Vê este craque no GAME FUT LIVE! ⚽', url: linkReal });
                  } catch (e) { console.log(e); }
                } else {
                  navigator.clipboard.writeText(linkReal);
                  alert("Link copiado! ⚽");
                }
              }}
              style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <Share2 size={30} color="white" />
              <span style={{ color: 'white', fontSize: '9px', fontWeight: 'bold', marginTop: '4px' }}>PARTILHAR</span>
            </div>

            {/* 4. PRESENTE (Invisível para o dono) */}
            {postSelecionado.userId !== auth.currentUser?.uid && (
              <div 
                onClick={() => { 
                  setIdPostAtivo(postSelecionado); 
                  setMostrarPresentes(true); 
                }} 
                style={{ cursor: 'pointer', fontSize: '32px' }}
              >
                🎁
              </div>
            )}
          </div>

          {/* CONTEÚDO */}
          {postSelecionado.tipo === 'video' ? (
            <video src={postSelecionado.url} style={{ width: '100%', maxHeight: '70vh' }} autoPlay loop playsInline controls muted={presenteAtivo !== null} />
          ) : (
            <img src={postSelecionado.url} style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} alt="Post" />
          )}

          <div style={{ padding: '20px', width: '100%' }}>
            <h3 style={{ color: '#39FF14', margin: 0 }}>@{postSelecionado.userName || 'Utilizador'}</h3>
            <p style={{ color: 'white', marginTop: '5px' }}>{postSelecionado.legenda}</p>
          </div>
        </div>
      )}

      {presenteAtivo && presenteAtivo.url && (
        <div style={{ 
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', 
          zIndex: 200000, display: 'flex', alignItems: 'center', 
          justifyContent: 'center', background: 'rgba(0,0,0,0.6)',
          pointerEvents: 'none'
        }}>
          <video 
            src={presenteAtivo.url} 
            autoPlay 
            playsInline
            onEnded={() => setPresenteAtivo(null)} 
            style={{ width: '100%', height: 'auto' }} 
          />
        </div>
      )}

          {/* ========================================================================= */}
      {/* 👥 JANELA DE SEGUIDORES COM LUPA, AVATAR E REDIRECIONAMENTO (MULTILÍNGUE) */}
      {/* ========================================================================= */}
            {mostrarModalSeguidores && (() => {
        // Correção de sintaxe síncrona com fallback direto
        const siglaIdioma = (navigator.language || navigator.userLanguage || "pt").toLowerCase().substring(0, 2);
        
        const dicionarioSg = {
          pt: { titulo: "SEGUIDORES ⚽", busca: "Pesquisar craque...", vazio: "Ainda sem seguidores... ⚽" },
          en: { titulo: "FOLLOWERS ⚽", busca: "Search player...", vazio: "No followers yet... ⚽" },
          es: { titulo: "SEGUIDORES ⚽", busca: "Buscar jugador...", vazio: "Sin seguidores aún... ⚽" },
          fr: { titulo: "ABONNÉS ⚽", busca: "Rechercher...", vazio: "Aucun abonné pour l'instant... ⚽" }
        };

        const tSg = dicionarioSg[siglaIdioma] || dicionarioSg.en;

        const seguidoresFiltrados = listaSeguidores.filter(s => 
          (s.nome || "").toLowerCase().includes(termoPesquisaSeguidores.toLowerCase())
        );

        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.82)', zIndex: 300000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '50px',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)'
          }}>
            <div style={{ 
              width: '92%', maxWidth: '400px', 
              background: 'linear-gradient(135deg, rgba(20, 24, 20, 0.95) 0%, rgba(10, 12, 10, 0.98) 100%)', 
              borderRadius: '24px', border: '1px solid #39FF14', padding: '20px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.8), 0 0 25px rgba(57, 255, 20, 0.25)',
              boxSizing: 'border-box', display: 'flex', flexDirection: 'column'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ color: '#39FF14', margin: 0, fontWeight: '950', fontSize: '15px', letterSpacing: '0.5px' }}>{tSg.titulo}</h3>
                <button 
                  onClick={() => { setMostrarModalSeguidores(false); setTermoPesquisaSeguidores(""); }}
                  style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff4444', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}
                >✕</button>
              </div>

              {/* BARRA DE PESQUISA REATIVA */}
              <div style={{ position: 'relative', marginBottom: '15px' }}>
                <input 
                  type="text"
                  placeholder={tSg.busca}
                  value={termoPesquisaSeguidores}
                  onChange={(e) => setTermoPesquisaSeguidores(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px 10px 35px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(0,0,0,0.4)', color: 'white', fontSize: '13px', fontWeight: '600', boxSizing: 'border-box', outline: 'none' }}
                />
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#39FF14', fontSize: '13px' }}>🔍</span>
              </div>

              {/* CONTENTOR DE SCROLL DA LISTA */}
              <div className="modal-scroll-limpo" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                                {seguidoresFiltrados.map((seg) => {
                  // BLINDAGEM: Captura o ID do utilizador de qualquer campo possível que venha mapeado da linha
                  const idUtilizadorAlvo = seg.uid || seg.id || seg.idDoc || seg.userId;
                  return (
                    <div 
                      key={idUtilizadorAlvo || Math.random().toString()} 
                      onClick={() => {
                        if (!idUtilizadorAlvo) {
                          console.error("Erro: O ID do utilizador alvo veio em branco da lista!");
                          return;
                        }
                        if (typeof setIdPostAtivo === 'function') {
                          setIdPostAtivo({ userId: idUtilizadorAlvo });
                        }
                        if (typeof setEcraAtivo === 'function') {
                          setEcraAtivo('perfil');
                        }
                        setMostrarModalSeguidores(false);
                        setTermoPesquisaSeguidores("");
                      }}
                      style={{ padding: '10px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '8px' }}
                    >
                      {seg.fotoUrl ? (
                        <img src={seg.fotoUrl} alt={seg.nome} style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(57, 255, 20, 0.3)' }} />
                      ) : (
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#25282a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#39FF14', fontWeight: '900', fontSize: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                          {(seg.nome || "C").substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <span style={{ color: 'white', fontWeight: '700', fontSize: '14px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seg.nome}</span>
                      <span style={{ color: '#39FF14', fontSize: '11px', fontWeight: 'bold' }}>➔</span>
                    </div>
                  );
                })}
                {seguidoresFiltrados.length === 0 && <p style={{ color: '#777', fontSize: '13px', textAlign: 'center', margin: '20px 0', fontWeight: '600' }}>{tSg.vazio}</p>}
              </div>
            </div>
          </div>
        );
      })()}

          {/* ========================================================================= */}
      {/* 🏃‍♂️ JANELA DE A SEGUIR COM LUPA, AVATAR E REDIRECIONAMENTO (MULTILÍNGUE) */}
      {/* ========================================================================= */}
            {mostrarModalSeguindo && (() => {
        // Correção de sintaxe síncrona com fallback estável
        const siglaIdioma = (navigator.language || navigator.userLanguage || "pt").toLowerCase().substring(0, 2);
        
        const dicionarioFl = {
          pt: { titulo: "A SEGUIR ⚽", busca: "Pesquisar craque...", vazio: "Ainda não segues nenhum craque... ⚽" },
          en: { titulo: "FOLLOWING ⚽", busca: "Search player...", vazio: "Not following anyone yet... ⚽" },
          es: { titulo: "SIGUIENDO ⚽", busca: "Buscar jugador...", vazio: "No sigues a nadie aún... ⚽" },
          fr: { titulo: "ABONNEMENTS ⚽", busca: "Rechercher...", vazio: "Aucun abonnement pour l'instant... ⚽" }
        };

        const tFl = dicionarioFl[siglaIdioma] || dicionarioFl.en;

        const seguindoFiltrados = listaSeguindo.filter(s => 
          (s.nome || "").toLowerCase().includes(termoPesquisaSeguindo.toLowerCase())
        );

        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.82)', zIndex: 300000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '50px',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)'
          }}>
            <div style={{ 
              width: '92%', maxWidth: '400px', 
              background: 'linear-gradient(135deg, rgba(20, 24, 20, 0.95) 0%, rgba(10, 12, 10, 0.98) 100%)', 
              borderRadius: '24px', border: '1px solid #39FF14', padding: '20px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.8), 0 0 25px rgba(57, 255, 20, 0.25)',
              boxSizing: 'border-box', display: 'flex', flexDirection: 'column'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ color: '#39FF14', margin: 0, fontWeight: '950', fontSize: '15px', letterSpacing: '0.5px' }}>{tFl.titulo}</h3>
                <button 
                  onClick={() => { setMostrarModalSeguindo(false); setTermoPesquisaSeguindo(""); }}
                  style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff4444', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}
                >✕</button>
              </div>

              {/* BARRA DE PESQUISA REATIVA */}
              <div style={{ position: 'relative', marginBottom: '15px' }}>
                <input 
                  type="text"
                  placeholder={tFl.busca}
                  value={termoPesquisaSeguindo}
                  onChange={(e) => setTermoPesquisaSeguindo(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px 10px 35px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(0,0,0,0.4)', color: 'white', fontSize: '13px', fontWeight: '600', boxSizing: 'border-box', outline: 'none' }}
                />
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#39FF14', fontSize: '13px' }}>🔍</span>
              </div>

              {/* CONTENTOR DE SCROLL DA LISTA */}
              <div className="modal-scroll-limpo" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                                {seguindoFiltrados.map((seg) => {
                  // BLINDAGEM: Captura o ID do utilizador de qualquer campo possível que venha mapeado da linha
                  const idUtilizadorAlvo = seg.uid || seg.id || seg.idDoc || seg.userId;
                  return (
                    <div 
                      key={idUtilizadorAlvo || Math.random().toString()} 
                      onClick={() => {
                        if (!idUtilizadorAlvo) {
                          console.error("Erro: O ID do utilizador alvo veio em branco da lista!");
                          return;
                        }
                        if (typeof setIdPostAtivo === 'function') {
                          setIdPostAtivo({ userId: idUtilizadorAlvo });
                        }
                        if (typeof setEcraAtivo === 'function') {
                          setEcraAtivo('perfil');
                        }
                        setMostrarModalSeguindo(false);
                        setTermoPesquisaSeguindo("");
                      }}
                      style={{ padding: '10px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '8px' }}
                    >
                      {seg.fotoUrl ? (
                        <img src={seg.fotoUrl} alt={seg.nome} style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(57, 255, 20, 0.3)' }} />
                      ) : (
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#25282a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#39FF14', fontWeight: '900', fontSize: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                          {(seg.nome || "C").substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <span style={{ color: 'white', fontWeight: '700', fontSize: '14px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seg.nome}</span>
                      <span style={{ color: '#39FF14', fontSize: '11px', fontWeight: 'bold' }}>➔</span>
                    </div>
                  );
                })}
                {seguindoFiltrados.length === 0 && <p style={{ color: '#777', fontSize: '13px', textAlign: 'center', margin: '20px 0', fontWeight: '600' }}>{tFl.vazio}</p>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* JANELA DE BLOQUEADOS EM VIDRO FOSCO */}
      {mostrarModalBloqueados && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 300001,
          display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '60px',
          backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)'
        }}>
          <div style={{ 
            width: '90%', maxWidth: '400px', 
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.01) 100%)', 
            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '20px', border: '1px solid #ff4444', padding: '20px',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5), 0 0 15px rgba(255, 68, 68, 0.15)',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#ff4444', margin: 0, fontWeight: '900', fontSize: '15px', letterSpacing: '0.5px' }}>LISTA NEGRA 🚫</h3>
              <button 
                onClick={() => setMostrarModalBloqueados(false)}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}
              >✕</button>
            </div>
                        {/* BARRA DE PESQUISA REATIVA COM LUPA */}
            <div style={{ position: 'relative', marginBottom: '15px', width: '100%' }}>
              <input 
                type="text"
                placeholder="Pesquisar bloqueado..."
                value={termoPesquisaBloqueados}
                onChange={(e) => setTermoPesquisaBloqueados(e.target.value)}
                style={{ width: '100%', padding: '8px 12px 8px 30px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(0,0,0,0.4)', color: 'white', fontSize: '13px', boxSizing: 'border-box', outline: 'none' }}
              />
              <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px' }}>🔍</span>
            </div>

            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {bloqueadosFiltrados.map((block) => (
                                <div key={block.uid} style={{ padding: '10px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: '700', fontSize: '14px', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden', flex: 1 }}>
                    {block.fotoUrl ? (
                      <img src={block.fotoUrl} alt={block.nome} style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(255,68,68,0.3)' }} />
                    ) : (
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#2e2020', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff4444', fontWeight: '900', fontSize: '11px', border: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
                        {(block.nome || "B").substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.nome}</span>
                  </div>
                                    <button 
                    onClick={async () => {
                      try {
                        // Faz a remoção direta usando o motor do auth global
                        const meuUid = auth?.currentUser?.uid;
                        if (!meuUid || !block.uid) return;
                        
                        // Importamos dinamicamente o método correto para evitar falhas de variáveis órfãs
                        const { getFirestore, doc, deleteDoc } = await import('firebase/firestore');
                        const firestoreInstancia = getFirestore();
                        
                        await deleteDoc(doc(firestoreInstancia, "utilizadores", meuUid, "bloqueados", block.uid));
                        setListaBloqueados(prev => prev.filter(b => b.uid !== block.uid));
                        console.log("Utilizador desbloqueado com sucesso!");
                      } catch (errBlock) {
                        console.error("Erro ao desbloquear:", errBlock.message);
                      }
                    }}
                    style={{ background: '#ff4444', color: 'black', border: 'none', padding: '5px 12px', borderRadius: '10px', fontWeight: '900', fontSize: '10px', cursor: 'pointer', textTransform: 'uppercase', flexShrink: 0 }}
                  >REMOVER</button>
                </div>
              ))}
              {bloqueadosFiltrados.length === 0 && <p style={{ color: '#888', fontSize: '13px', textAlign: 'center', margin: '20px 0' }}>Ninguém bloqueado. ⚽</p>}
            </div>
          </div>

                {/* JANELA DA LISTA DE LIKES EM VIDRO FOSCO */}
      {mostrarListaLikes && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.75)', zIndex: 10000, display: 'flex', flexDirection: 'column', padding: '20px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingTop: '40px' }}>
            <h2 style={{ color: '#39FF14', margin: 0, fontWeight: '900', fontSize: '18px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Quem gostou ⚽</h2>
            <button 
              onClick={() => setMostrarListaLikes(false)} 
              style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', fontSize: '16px', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}
            >
              ✕
            </button>
          </div>
          
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {postSelecionado?.quemDeuLike?.length > 0 ? (
              postSelecionado.quemDeuLike.map(userId => (
                <div 
                  key={userId} 
                  onClick={() => {
                    setMostrarListaLikes(false);
                    window.dispatchEvent(new CustomEvent('abrirPerfilUsuario', { detail: userId }));
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '14px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)', borderRadius: '12px', marginBottom: '8px', border: '1px solid rgba(255,255,255,0.04)' }}
                >
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#39FF14', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', color: 'black', fontSize: '14px', boxShadow: '0 2px 8px rgba(57,255,20,0.3)' }}>
                    ⚽
                  </div>
                  <span style={{ color: 'white', fontWeight: '700', fontSize: '14px' }}>Ver perfil do craque</span>
                </div>
              ))
            ) : (
              <p style={{ color: '#888', textAlign: 'center', marginTop: '50px', fontSize: '13px', fontWeight: '500' }}>Ainda ninguém deu like nesta jogada. ⚽</p>
            )}
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

export default Perfil;


